import { useSearch } from '@backstage/plugin-search-react';
import { CommandGroup, CommandItem, isExternalLink } from '@vivicta/ui';
import { ExternalLink } from 'lucide-react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { resultTypeIcon, resultTypeLabel, writeSearchUrlState } from './utils';

const TERM_DEBOUNCE_MS = 200;
const PALETTE_RESULT_LIMIT = 6;

export type PaletteSearchResultsProps = {
  /** The palette's raw input value. Debounced before it reaches the search API. */
  query: string;
  /** Closes the palette. Called before navigating away from a picked result. */
  onDismiss: () => void;
  /** Reports whether hits for `query` are still on their way, so the palette
   * doesn't claim "no matches" a moment before they land. */
  onPendingChange?: (pending: boolean) => void;
};

/**
 * The live full-text half of the command palette: renders backend search hits
 * as one more `CommandGroup` alongside Pages, Create and Actions, so a single
 * ⌘K/`/` surface answers both "jump to a page I know" and "find a thing I
 * don't". Renders nothing at all until there is a query with hits, keeping the
 * palette's static groups the whole story while the user is still typing.
 *
 * Must be mounted inside a `SearchContextProvider` and a cmdk `CommandList`.
 */
export const PaletteSearchResults = ({
  query,
  onDismiss,
  onPendingChange,
}: PaletteSearchResultsProps) => {
  const navigate = useNavigate();
  const { setTerm, term, result } = useSearch();

  useEffect(() => {
    const timer = setTimeout(() => setTerm(query), TERM_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, setTerm]);

  // Covers the debounce window as well as the request itself: until `term`
  // catches up with `query`, anything on screen belongs to the previous term.
  const pending = query !== term || result.loading;

  useEffect(() => {
    onPendingChange?.(pending);
  }, [pending, onPendingChange]);

  // Results stay on screen while the next term is in flight — `useAsync` keeps
  // the previous value during a reload, and dropping it on every keystroke made
  // the palette flicker. The `/search` page owns the full loading/error story.
  if (!query || result.error) {
    return null;
  }

  const results = (result.value?.results ?? []).slice(0, PALETTE_RESULT_LIMIT);

  if (results.length === 0) {
    return null;
  }

  const open = (location: string) => {
    onDismiss();
    if (isExternalLink(location)) {
      window.open(location, '_blank', 'noopener,noreferrer');
    } else {
      navigate(location);
    }
  };

  const seeAllResults = () => {
    onDismiss();
    const search = writeSearchUrlState({ term: query, types: [], filters: {} });
    navigate(search ? `/search?${search}` : '/search');
  };

  return (
    <CommandGroup heading="Search results">
      {results.map(({ document, type }) => {
        const Icon = resultTypeIcon(type);
        return (
          <CommandItem
            key={`${type}:${document.location}`}
            value={`search:${type}:${document.location}`}
            onSelect={() => open(document.location)}
          >
            <Icon aria-hidden="true" className="mr-2 size-4 shrink-0" />
            <span className="truncate">{document.title}</span>
            <span className="ml-2 shrink-0 text-xs text-muted-foreground">
              {resultTypeLabel(type)}
            </span>
            {isExternalLink(document.location) && (
              <ExternalLink
                aria-hidden="true"
                className="ml-1.5 size-3.5 shrink-0 text-muted-foreground"
              />
            )}
          </CommandItem>
        );
      })}
      <CommandItem value="search:see-all" onSelect={seeAllResults}>
        <span className="text-muted-foreground">
          See all results for “{query}”
        </span>
      </CommandItem>
    </CommandGroup>
  );
};
